// Wave 20.64 — Public /terms page. Linked from the marketing footer,
// every auth screen, and the iOS app's Account screen (App Review
// guideline 3.1.2 wants the Terms of Use reachable inside the binary).
// Fetches the current version of the seeded terms from
// GET /public/legal/terms (no auth required) and renders the body.
//
// Same shape as Privacy.tsx on purpose — one fetch, one card, no
// client-side copy. The document lives in the database so it can be
// versioned and re-accepted without a web deploy.

import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import PageLayout from './components/PageLayout';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';

interface LegalDoc {
  kind: 'terms';
  version: string;
  effectiveAt: string;
  body: string;
}

export default function Terms() {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/public/legal/terms`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        setDoc(await r.json());
      })
      .catch((e) => setError(e?.message || 'Failed to load'));
  }, []);

  return (
    <PageLayout maxWidth="md">
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
              AeroChat Inc.
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Terms of Service
            </Typography>
            {doc && (
              <Typography sx={{ color: colors.textMuted, mt: 0.5, fontSize: '0.85rem' }}>
                Effective {new Date(doc.effectiveAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>

          {error && (
            <Typography sx={{ color: colors.danger, fontSize: '0.9rem' }}>
              Couldn't load the terms of service: {error}
            </Typography>
          )}

          {!doc && !error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: colors.brand }} />
            </Box>
          )}

          {doc && (
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.7,
                color: colors.text,
                fontSize: '0.95rem',
              }}
            >
              {doc.body}
            </Box>
          )}
        </Stack>
      </Container>
    </PageLayout>
  );
}
